import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';

export interface SkillPackSkill {
  id: number;
  packId: number;
  skillName: string;
  skillFullName: string;
  skillDescription: string | null;
  skillUrl: string | null;
  position: number;
  addedAt: string | null;
}

export interface SkillPack {
  id: number;
  userId: number;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  createdAt: string | null;
  updatedAt: string | null;
  skills: SkillPackSkill[];
}

export interface PackAssignment {
  assignmentId: number;
  packId: number;
  personaId: string | null;
  assignedAt: string | null;
  packName: string;
  packDescription: string | null;
  packIcon: string;
  packColor: string;
  personaName: string | null;
  personaIcon: string | null;
  skills: SkillPackSkill[];
}

const PACKS_KEY = ['skillpacks'];
const ASSIGNMENTS_KEY = ['skillpacks', 'assignments'];

export function useSkillPacks(enabled = true) {
  return useQuery({
    queryKey: PACKS_KEY,
    queryFn: async (): Promise<SkillPack[]> => {
      const res = await fetch('/_api/skillpacks/list', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch skill packs');
      const data = superjson.parse<{ packs: SkillPack[] }>(await res.text());
      return data.packs;
    },
    enabled,
    staleTime: 10000,
  });
}

export function useCreateSkillPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      icon?: string;
      color?: string;
      skills: Array<{
        skillName: string;
        skillFullName: string;
        skillDescription?: string;
        skillUrl?: string;
      }>;
    }) => {
      const res = await fetch('/_api/skillpacks/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to create skill pack');
      return superjson.parse<{ pack: SkillPack }>(await res.text());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACKS_KEY });
    },
  });
}

export function useUpdateSkillPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      packId: number;
      name?: string;
      description?: string;
      icon?: string;
      color?: string;
      skills?: Array<{
        skillName: string;
        skillFullName: string;
        skillDescription?: string;
        skillUrl?: string;
      }>;
    }) => {
      const res = await fetch('/_api/skillpacks/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to update skill pack');
      return superjson.parse<{ pack: SkillPack }>(await res.text());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACKS_KEY });
    },
  });
}

export function useDeleteSkillPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: number) => {
      const res = await fetch('/_api/skillpacks/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ packId }),
      });
      if (!res.ok) throw new Error('Failed to delete skill pack');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });
    },
  });
}

export function useAssignSkillPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      packId: number;
      sessionId: string;
      personaId?: string | null;
      action: 'assign' | 'unassign';
    }) => {
      const res = await fetch('/_api/skillpacks/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to assign skill pack');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });
    },
  });
}

export function useSessionPackAssignments(sessionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ASSIGNMENTS_KEY, sessionId],
    queryFn: async (): Promise<PackAssignment[]> => {
      const res = await fetch(`/_api/skillpacks/assignments?sessionId=${sessionId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch assignments');
      const data = superjson.parse<{ assignments: PackAssignment[] }>(await res.text());
      return data.assignments;
    },
    enabled: !!sessionId && enabled,
    staleTime: 10000,
  });
}
